import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { useApolloClient } from '@apollo/react-hooks';
// components
import { ItemsNavigator, Spinner, Icons } from '@ems/client-design-system';
import { ProfileGraph } from 'src/components/ProfileGraph';
// containers
import { OperationDetailsContainer, OperationWeatherContainer } from 'src/containers';
import { MapContainer } from 'src/@operations/containers/Summary';
import {
  fetchOperationDetails,
  fetchInAirTracksForPlayback,
} from 'src/@operations/resolvers/operationSummaryResolver';
import { SummaryHeader } from 'src/components/PageHeader';
// selectors
import { useConfigSelectors } from 'src/@app/reducers/configReducer';
import { useLanguageSelectors } from 'src/@app/reducers/languageReducer';
// functions
import { history, getDeployedProductId, rememberSavedFilters } from 'src/utils';
import { getPaginationInformation, goBack, navigate } from 'src/@app/functions/itemsNavigation';
// constants
import { OPERATION } from 'src/constants';
import { PlaybackControl } from 'src/components/PlaybackControl';
import { useFilterDataSelectors } from 'src/@app/reducers';

export const ContentContainer = ({ id, path, paginationInfo }) => {
  const [operation, updateOperation]: any = useState({});
  const [inAirData, setInAirData] = useState<any>([]);
  const client = useApolloClient();
  // filters data
  const filtersSelectors = useFilterDataSelectors();
  const operationFilterData = filtersSelectors.getOperationsFilterData();
  // Configuration
  const configSelectors = useConfigSelectors();
  const {
    operationDetails: { showWeatherPanel },
  } = configSelectors.getConfig();
  // Translation
  const languageSelectors = useLanguageSelectors();
  const {
    screens: {
      operations: { title: goBackTitle },
    },
  } = languageSelectors.getLanguage();
  // Playback states
  const [isPlaybackMode, setIsPlaybackMode] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [, setRunningStatus] = useState<boolean>(true);
  const [, setDraggingStatus] = useState<boolean>(false);
  const [, setPlaybackSpeed] = useState<number>(0);

  const getCenterPoint = (points, operationType) => {
    let position = { altitude: 0, latitude: 0, longitude: 0 };
    if (typeof points !== 'undefined' && points.length > 2) {
      const at = operationType === 'Arrival' ? points.length - 1 : 0;
      position = { altitude: points[at].alt, latitude: points[at].lat, longitude: points[at].lon };
    }
    return position;
  };

  const resetPageData = () => {
    updateOperation({});
    setInAirData([]);
  };

  useEffect(() => {
    rememberSavedFilters(OPERATION);
    if (id) {
      fetchOperationDetails(client, id)
        .then(({ data }: any) => {
          updateOperation(data);
        })
        .catch(error => {
          history.replace(`/${getDeployedProductId()}/404`);
          console.error(error);
        });
    }
  }, [id]);

  useEffect(() => {
    if (operation && operation.time) {
      fetchInAirTracksForPlayback(client, operation.time, operationFilterData.airportIds).then(
        (data: any) => {
          setInAirData(data.data);
        }
      );
    }
  }, [operation]);

  const { id: operationId, operationType, acid: pageTitle, time, points, correlated } = operation;
  const position = getCenterPoint(points, operationType);
  const hasWeatherData: boolean =
    typeof correlated !== 'undefined' && correlated.hasOwnProperty('weather') && correlated.weather;
  const hasData: boolean = !!Object.keys(operation).length;
  const paging = getPaginationInformation(Number(id), paginationInfo);

  if (!hasData) {
    return (
      <>
        <div className="map-skeleton">
          <Spinner loading={true} size="xl" centered={true} />
        </div>
        <div className="content-skeleton" />
      </>
    );
  }

  return (
    <>
      <div className="map_wrapper">
        <div className="map">
          <MapContainer
            time={time}
            operationId={operationId}
            operation={operation}
            position={position}
            isPlaybackMode={isPlaybackMode}
            currentTime={currentTime}
            inAirData={inAirData}
          />
        </div>
        <div className="playback-slider">
          <ProfileGraph
            data={operation.profile}
            operationType={operation.operationType}
            currentTime={isPlaybackMode ? currentTime : 0}
            startTime={operation.startTime}
            endTime={operation.endTime}
            shouldDisplay={operation && operation.profile}
          />
          {isPlaybackMode && operation.startTime && (
            <>
              <PlaybackControl
                startTime={operation.startTime}
                endTime={operation.endTime}
                onPositionUpdate={setCurrentTime}
                onPlaybackSpeedUpdate={setPlaybackSpeed}
                onRunningStatusUpdate={setRunningStatus}
                onDraggingStatusUpdate={setDraggingStatus}
                playbackTimeBuffer={0}
                alwaysShowDot={true}
                profileData={operation.profile}
                showProfileData={true}
              />
            </>
          )}
          {!isPlaybackMode && (
            <button
              className={cx('mode-playback', {
                'mode-playback--hidden': isPlaybackMode,
              })}
              onClick={() => setIsPlaybackMode(!isPlaybackMode)}>
              <Icons iconName={`ic-ui-play`} size="24" fill="#2e384d" />
            </button>
          )}
        </div>
      </div>
      <div className="container-fluid container-fluid--details">
        <div className="container-fluid--inner">
          <SummaryHeader type="navigation">
            <ItemsNavigator
              languageData={{ goBackTitle }}
              goBack={() => goBack(path, paging, [])}
              navigateItems={paging.navigateItems}
              nextItem={navigate('forward', path, paging, () => {
                resetPageData();
              })}
              previousItem={navigate('backward', path, paging, () => {
                resetPageData();
              })}
            />
          </SummaryHeader>
          <SummaryHeader type="summary">
            <div className="page-header_title">{pageTitle}</div>
          </SummaryHeader>
          <div className="layout_split">
            <div className="layout_split--half">
              <div className="layout_content">
                <OperationDetailsContainer operation={operation} />
                {showWeatherPanel && hasWeatherData && (
                  <OperationWeatherContainer data={hasWeatherData} />
                )}
              </div>
            </div>
            <div className="layout_split--half">
              <div className="layout_content" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
